import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const formRef = useRef<HTMLDivElement>(null);

  // Entrance animation
  useGSAP(() => {
    const els = formRef.current?.querySelectorAll('.anim-in');
    if (els) {
      gsap.from(els, { y: 20, opacity: 0, stagger: 0.08, duration: 0.5, ease: 'power3.out' });
    }
  }, []);

  const login = useMutation({
    mutationFn: () => authApi.login(username, password),
    onSuccess: (data) => {
      qc.setQueryData(['auth', 'me'], data.user);
      gsap.to(formRef.current, {
        opacity: 0, y: -20, duration: 0.3, ease: 'power2.in',
        onComplete: () => navigate(from, { replace: true }),
      });
    },
    onError: () => {
      setError('Invalid username or password.');
      gsap.fromTo(formRef.current,
        { x: -10 },
        { x: 0, duration: 0.5, ease: 'elastic.out(1,0.3)' },
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    login.mutate();
  };

  return (
    <div className="flex h-screen">
      {/* Left — hero image */}
      <div className="hidden lg:flex flex-1 items-end bg-dark overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-br from-dark via-dark/80 to-primary/20" />
        {/* Rising sun decorative */}
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
          <circle cx="200" cy="200" r="60" fill="#C8102E"/>
          {Array.from({ length: 24 }, (_, i) => {
            const angle = (i * 15) * Math.PI / 180;
            return (
              <line key={i}
                x1={200 + 80 * Math.cos(angle)} y1={200 + 80 * Math.sin(angle)}
                x2={200 + 200 * Math.cos(angle)} y2={200 + 200 * Math.sin(angle)}
                stroke="#C8102E" strokeWidth="2"
              />
            );
          })}
        </svg>
        <div className="relative z-10 p-10 pb-16">
          <p className="font-heading text-5xl text-white tracking-widest">Sakura</p>
          <p className="font-body text-white/50 text-sm tracking-wider mt-1">VEHICLE MANAGER</p>
          <p className="font-body text-white/30 text-xs mt-4 max-w-xs">
            Internal tool for managing FiveM vehicle handling, classes, tiers, and deployment status.
          </p>
        </div>
      </div>

      {/* Right — login form */}
      <div className="flex flex-1 items-center justify-center bg-white px-8">
        <div ref={formRef} className="w-full max-w-sm space-y-6">

          <div className="anim-in space-y-1">
            <div className="flex items-center gap-3 mb-4">
              <svg width="32" height="32" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="5" fill="#C8102E"/>
                {[0,30,60,90,120,150,180,210,240,270,300,330].map(deg => (
                  <line key={deg}
                    x1={14 + 7 * Math.cos(deg * Math.PI / 180)} y1={14 + 7 * Math.sin(deg * Math.PI / 180)}
                    x2={14 + 12 * Math.cos(deg * Math.PI / 180)} y2={14 + 12 * Math.sin(deg * Math.PI / 180)}
                    stroke="#C8102E" strokeWidth="1.5"
                  />
                ))}
              </svg>
              <h1 className="font-heading text-3xl text-dark tracking-widest">Sakura</h1>
            </div>
            <h2 className="font-heading text-xl text-dark">Sign In</h2>
            <p className="font-body text-text-muted text-sm">Vehicle Manager — Internal Access</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="anim-in">
              <label className="label">Username</label>
              <input
                className="input"
                type="text"
                autoComplete="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div className="anim-in">
              <label className="label">Password</label>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <div className="anim-in border border-primary bg-red-50 px-3 py-2 text-primary text-xs font-body">
                {error}
              </div>
            )}

            <div className="anim-in">
              <button
                type="submit"
                disabled={login.isPending}
                className="w-full py-2.5 bg-dark text-white font-heading uppercase tracking-widest hover:bg-primary transition-colors"
              >
                {login.isPending ? 'Signing in…' : 'Sign In'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
